import React, { useRef } from 'react';
import { observer, useAsObservableSource } from 'mobx-react-lite';
import { useAppStateContext } from '../../../AppState/AppState';
import {
    CampaignName,
    ContentContainer,
    DatesContainer,
    DateTitle,
    DateValue,
    DateWrapper,
    ImageContainer,
    TextAreaCopyComponent,
    Indicator,
    IndicatorsWrapper,
    ActiveLabel,
    LinkWrapper,
    BannerWrapper,
    BannerLink,
    VideoWrapper,
    CopyComponentWrapper,
} from './NotificationPageListItem.style';
import { NotificationPageState } from 'src/AppState/NotificationPageState/NotificationPageState';
import { PromoNotificationsModelType } from 'src/api/config/promo_notifications/get_promo_notifications';
import moment from 'moment';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { observable, makeAutoObservable } from 'mobx';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import {
    ListItem,
    ActionsWrapper,
    TrashIconWrapper,
    ButtonsWrapper,
} from 'src/domains/layouts/shared/cms/list/List.style';
import { MoveUpDown } from 'src/domains/layouts/shared/moreActions/MoveUpDown';

interface NotificationPagePropsType {
    notificationPage: PromoNotificationsModelType;
}

enum ShowActionEnum {
    DIRECT = 'direct-link',
    EVENT = 'event-id',
    COMPETITION = 'competition-id',
    CASINO = 'casino-game-id',
    SPORT = 'sport',
}

enum BannerEnum {
    HTML = 'html',
    STATIC = 'static',
    VIDEO = 'video',
}
class State {
    @observable copySuccess = false;
    constructor(
        private readonly notificationPageState: NotificationPageState,
        private readonly notificationPage: PromoNotificationsModelType,
        public readonly textAreaRef: React.RefObject<HTMLTextAreaElement>
    ) {
        makeAutoObservable(this);
    }

    onChangePageToEdit = (): void => {
        this.notificationPageState.setPage('edit');
        this.notificationPageState.setEditedNotificationPage(this.notificationPage);
    };

    onRemove = (): void => {
        this.notificationPageState.onRemove(this.notificationPage.id);
    };

    changeActiveFlag = (): void => {
        this.notificationPageState.setActiveFlag(this.notificationPage);
    };

    copyToClipboard = (e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        this.textAreaRef !== null && this.textAreaRef.current && this.textAreaRef.current.select();
        document.execCommand('copy');
        const target = e.target as HTMLDivElement;
        target.focus();
        this.copySuccess = true;
        setTimeout(() => {
            this.copySuccess = false;
        }, 3200);
    };

    showActionLabel = (value: string): string => {
        switch (value) {
            case ShowActionEnum.DIRECT:
                return 'Direct link';
            case ShowActionEnum.EVENT:
                return 'Event';
            case ShowActionEnum.COMPETITION:
                return 'Competition';
            case ShowActionEnum.CASINO:
                return 'Casino';
            case ShowActionEnum.SPORT:
                return 'Sport';
            default:
                return '';
        }
    };

    showBannerLabel = (value: string): string => {
        switch (value) {
            case BannerEnum.HTML:
                return 'HTML5 Promo';
            case BannerEnum.STATIC:
                return 'Image Promo';
            case BannerEnum.VIDEO:
                return 'Video Promo';
            default:
                return '';
        }
    };

    getCopyLink = (): number => {
        return this.notificationPage.id;
    };

    moveItemUp = (): void => {
        this.notificationPageState.moveItemUp(this.notificationPage.id);
    };

    moveItemDown = (): void => {
        this.notificationPageState.moveItemDown(this.notificationPage.id);
    };
}

export const NotificationPageListItem = observer(({ notificationPage }: NotificationPagePropsType) => {
    const { notificationPageState } = useAppStateContext();
    const lpIn = useAsObservableSource(notificationPage);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [state] = React.useState(() => new State(notificationPageState, lpIn, textAreaRef));
    const startDate = moment(notificationPage.date_from).format('DD-MM-YYYY hh:mm:ss');
    const endDate = moment(notificationPage.date_to).format('DD-MM-YYYY hh:mm:ss');
    const isEdited = notificationPage.id === notificationPageState.editedNotificationPage?.id;

    return (
        <ListItem
            isEdited={isEdited}
            homepage={false}
            boxHeight='218px'
            direction='row'
        >
            <DisplayResource notificationPage={notificationPage} />
            <ContentContainer>
                <CampaignName data-test='promo-name'>{notificationPage.title}</CampaignName>
                <DatesContainer>
                    <DateWrapper isStart>
                        <DateTitle>Scheduled start</DateTitle>
                        <DateValue>{startDate}</DateValue>
                    </DateWrapper>
                    <DateWrapper>
                        <DateTitle>Scheduled finish</DateTitle>
                        <DateValue>{endDate}</DateValue>
                    </DateWrapper>
                </DatesContainer>

                <IndicatorsWrapper>
                    {notificationPage.action_template !== '' ? (
                        <Indicator data-test='promo-action-type'>
                            {state.showActionLabel(notificationPage.action_template)}
                        </Indicator>
                    ) : null}
                    {notificationPage.media_type !== '' ? (
                        <Indicator data-test='promo-banner-type'>
                            {state.showBannerLabel(notificationPage.media_type)}
                        </Indicator>
                    ) : null}
                </IndicatorsWrapper>
                <Checkbox
                    labelSide='right'
                    onChange={state.changeActiveFlag}
                    isChecked={notificationPage.is_active}
                    isDisabled={false}
                    dataTest='promo-publish-checkbox'
                >
                    <ActiveLabel>Published</ActiveLabel>
                </Checkbox>

                <CopyComponentWrapper>
                    <TextAreaCopyComponent
                        ref={state.textAreaRef}
                        defaultValue={state.getCopyLink()}
                    />
                </CopyComponentWrapper>

                <ButtonsWrapper>
                    <MoveUpDown
                        moveItemUp={state.moveItemUp}
                        moveItemDown={state.moveItemDown}
                    />
                    <ActionsWrapper>
                        <Button
                            version='secondary'
                            size='small'
                            onClick={state.onChangePageToEdit}
                        >
                            Edit
                        </Button>
                        <TrashIconWrapper onClick={state.onRemove} />
                    </ActionsWrapper>
                </ButtonsWrapper>
            </ContentContainer>
        </ListItem>
    );
});

export const DisplayResource = observer(({ notificationPage }: NotificationPagePropsType) => {
    if (notificationPage.media_type === BannerEnum.HTML) {
        return (
            <BannerWrapper>
                <LinkWrapper>
                    <BannerLink
                        target='_blank'
                        href={notificationPage.html_url ?? ''}
                    >
                        Show HTML banner
                    </BannerLink>
                </LinkWrapper>
            </BannerWrapper>
        );
    }
    if (notificationPage.media_type === BannerEnum.STATIC) {
        return <ImageContainer imgSrc={notificationPage.img_url ?? ''} />;
    }
    if (notificationPage.media_type === BannerEnum.VIDEO) {
        return (
            <VideoWrapper>
                <iframe
                    title={notificationPage.title}
                    src={notificationPage.video_url ?? ''}
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    width='176'
                    height='168'
                />
            </VideoWrapper>
        );
    }
    return null;
});
